import { useDroppable } from "@dnd-kit/core";
import type { ProjectSummary } from "@/hooks/useProjectBoardData";
import type { Tables } from "@/types/database.types";
import { useState } from "react";
import { ProjectEditor } from "./ProjectEditor";

interface ProjectSectionProps extends React.PropsWithChildren {
  title: string;
  count: number;
  /** Optional hover help on the section title (used by the archive page). */
  infoText?: string;
  onAddProject?: () => void;
  /** Deal-stage id new projects in this section start at. */
  defaultDealStageId: string;
  /** The category id new projects added here are created in. */
  defaultCategory?: string;
  onSave?: () => void;
  onOptimisticCreate?: (project: ProjectSummary) => void;
  onOptimisticConfirm?: (projectId: string, project: Tables<"projects">) => void;
  onOptimisticError?: (projectId: string) => void;
  droppableId?: string;
}

/**
 * Section component grouping related project groups
 */
export function ProjectSection({
  title,
  count,
  children,
  infoText,
  onAddProject,
  defaultDealStageId,
  defaultCategory,
  onSave,
  onOptimisticCreate,
  onOptimisticConfirm,
  onOptimisticError,
  droppableId,
}: ProjectSectionProps) {
  const [isCreating, setIsCreating] = useState(false);
  const { setNodeRef, isOver } = useDroppable({ id: droppableId ?? title });

  const handleAddClick = () => {
    setIsCreating(true);
  };

  const handleSave = () => {
    setIsCreating(false);
    onSave?.();
  };

  const handleCancel = () => {
    setIsCreating(false);
  };

  return (
    <section
      className={`project-section${isOver ? " project-section--drag-over" : ""}`}
      ref={setNodeRef}
    >
      <div className="project-section__header">
        <div title={infoText}>
          {title} ({count})
        </div>
        {!isCreating && (
          <button
            className="secondary-button small-button"
            type="button"
            onClick={handleAddClick}
            data-testid="new-project-button"
          >
            + New Project
          </button>
        )}
      </div>
      <div className="project-section__projects">
        {isCreating && (
          <ProjectEditor
            defaultDealStageId={defaultDealStageId}
            defaultCategory={defaultCategory}
            onSave={handleSave}
            onCancel={handleCancel}
            onOptimisticCreate={onOptimisticCreate}
            onOptimisticConfirm={onOptimisticConfirm}
            onOptimisticError={onOptimisticError}
          />
        )}
        {count === 0 && !isCreating ? (
          <div className="project-section__empty">
            No projects in this section yet.
          </div>
        ) : (
          children
        )}
      </div>
    </section>
  );
}
